import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Bell, Plus, MapPin, Route, Trash2, ToggleLeft, ToggleRight, 
  Loader2, ChevronRight, Zap, AlertCircle, Check, X, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const US_REGIONS = [
  { value: 'northeast', label: 'Northeast' },
  { value: 'southeast', label: 'Southeast' },
  { value: 'midwest', label: 'Midwest' },
  { value: 'southwest', label: 'Southwest' },
  { value: 'west', label: 'West' },
];

const AlertsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isCarrier } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [alertType, setAlertType] = useState('lane');
  const [formData, setFormData] = useState({
    name: '',
    origin_state: '',
    origin_city: '',
    origin_region: '',
    dest_state: '',
    dest_city: '',
    dest_region: '',
    current_location: '',
    radius_miles: '100',
    load_categories: [],
    min_price: '',
    max_price: '',
    notification_method: 'in_app',
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchAlerts();
    }
  }, [isAuthenticated]);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/alerts');
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = async () => {
    if (!formData.name) {
      toast.error('Please enter an alert name');
      return;
    }

    if (alertType === 'lane' && !formData.origin_state && !formData.origin_city && !formData.origin_region) {
      toast.error('Please specify at least an origin location for lane alerts');
      return;
    }

    if (alertType === 'location' && !formData.current_location) {
      toast.error('Please specify a location for location-based alerts');
      return;
    }

    setCreating(true);
    try {
      const alertData = {
        alert_type: alertType,
        name: formData.name,
        notification_method: formData.notification_method,
        load_categories: formData.load_categories,
        min_price: formData.min_price ? parseFloat(formData.min_price) : null,
        max_price: formData.max_price ? parseFloat(formData.max_price) : null,
      };

      if (alertType === 'lane') {
        alertData.origin_state = formData.origin_state === 'any_state' ? null : (formData.origin_state || null);
        alertData.origin_city = formData.origin_city || null;
        alertData.origin_region = formData.origin_region || null;
        alertData.dest_state = formData.dest_state === 'any_state' ? null : (formData.dest_state || null);
        alertData.dest_city = formData.dest_city || null;
        alertData.dest_region = formData.dest_region || null;
      } else {
        alertData.current_location = formData.current_location;
        alertData.radius_miles = parseInt(formData.radius_miles) || 100;
      }

      await api.post('/alerts', alertData);
      toast.success('Alert created successfully!');
      setShowCreateDialog(false);
      resetForm();
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alert');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleAlert = async (alertId, currentStatus) => {
    try {
      await api.put(`/alerts/${alertId}/toggle`);
      setAlerts(alerts.map(a => 
        a.id === alertId ? { ...a, is_active: !currentStatus } : a
      ));
      toast.success(currentStatus ? 'Alert paused' : 'Alert activated');
    } catch (error) {
      toast.error('Failed to toggle alert');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) return;
    
    try {
      await api.delete(`/alerts/${alertId}`);
      setAlerts(alerts.filter(a => a.id !== alertId));
      toast.success('Alert deleted');
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      origin_state: '',
      origin_city: '',
      origin_region: '',
      dest_state: '',
      dest_city: '',
      dest_region: '',
      current_location: '',
      radius_miles: '100',
      load_categories: [],
      min_price: '',
      max_price: '',
      notification_method: 'in_app',
    });
    setAlertType('lane');
  };

  const toggleCategory = (category) => {
    const current = formData.load_categories;
    if (current.includes(category)) {
      setFormData({ ...formData, load_categories: current.filter(c => c !== category) });
    } else {
      setFormData({ ...formData, load_categories: [...current, category] });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 pt-20 flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-gray-200 bg-white">
          <CardContent className="pt-6 text-center">
            <Bell className="w-12 h-12 mx-auto text-[#3B82F6] mb-4" />
            <h2 className="text-xl font-bold mb-2">Sign In Required</h2>
            <p className="text-gray-600 mb-4">
              Please sign in to manage your backhaul alerts.
            </p>
            <Button onClick={() => navigate('/login')}>
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-20 pb-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0D1B2A] flex items-center gap-3">
              <Bell className="w-8 h-8 text-green-600" />
              Backhaul Alerts
            </h1>
            <p className="text-gray-600 mt-2">
              Get notified when loads match your preferred lanes or locations
            </p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#4ADE80] hover:bg-[#22C55E] text-black font-semibold"
                data-testid="create-alert-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl">Create Backhaul Alert</DialogTitle>
                <DialogDescription>
                  Set up alerts to get notified when matching loads are posted
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                {/* Alert Type Toggle */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setAlertType('lane')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      alertType === 'lane'
                        ? 'border-[#3B82F6] bg-[#3B82F6]/10'
                        : 'border-gray-200 hover:border-[#3B82F6]/40'
                    }`}
                  >
                    <Route className={`w-6 h-6 mx-auto mb-2 ${alertType === 'lane' ? 'text-[#3B82F6]' : 'text-gray-600'}`} />
                    <div className={`font-medium ${alertType === 'lane' ? 'text-[#0D1B2A]' : 'text-gray-600'}`}>Lane-Based</div>
                    <div className="text-xs text-gray-500 mt-1">Origin → Destination</div>
                  </button>
                  <button
                    onClick={() => setAlertType('location')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      alertType === 'location'
                        ? 'border-[#4ADE80] bg-[#4ADE80]/10'
                        : 'border-gray-200 hover:border-[#4ADE80]/40'
                    }`}
                  >
                    <MapPin className={`w-6 h-6 mx-auto mb-2 ${alertType === 'location' ? 'text-green-600' : 'text-gray-600'}`} />
                    <div className={`font-medium ${alertType === 'location' ? 'text-[#0D1B2A]' : 'text-gray-600'}`}>Location-Based</div>
                    <div className="text-xs text-gray-500 mt-1">Loads near you</div>
                  </button>
                </div>

                {/* Alert Name */}
                <div>
                  <Label>Alert Name</Label>
                  <Input
                    placeholder={alertType === 'lane' ? "e.g., Texas to California" : "e.g., Loads near Dallas"}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2 bg-[#0A1628] border-gray-200"
                  />
                </div>

                {/* Lane-Based Fields */}
                {alertType === 'lane' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-green-400">Origin State</Label>
                        <Select
                          value={formData.origin_state}
                          onValueChange={(value) => setFormData({ ...formData, origin_state: value })}
                        >
                          <SelectTrigger className="mt-2 bg-[#0A1628] border-gray-200">
                            <SelectValue placeholder="Any state" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 max-h-60">
                            <SelectItem value="any_state">Any state</SelectItem>
                            {US_STATES.map(state => (
                              <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-red-400">Destination State</Label>
                        <Select
                          value={formData.dest_state}
                          onValueChange={(value) => setFormData({ ...formData, dest_state: value })}
                        >
                          <SelectTrigger className="mt-2 bg-[#0A1628] border-gray-200">
                            <SelectValue placeholder="Any state" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 max-h-60">
                            <SelectItem value="any_state">Any state</SelectItem>
                            {US_STATES.map(state => (
                              <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-600">Or Origin City</Label>
                        <Input
                          placeholder="e.g., Houston"
                          value={formData.origin_city}
                          onChange={(e) => setFormData({ ...formData, origin_city: e.target.value })}
                          className="mt-1 bg-[#0A1628] border-gray-200"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Or Destination City</Label>
                        <Input
                          placeholder="e.g., Los Angeles"
                          value={formData.dest_city}
                          onChange={(e) => setFormData({ ...formData, dest_city: e.target.value })}
                          className="mt-1 bg-[#0A1628] border-gray-200"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Location-Based Fields */}
                {alertType === 'location' && (
                  <div className="space-y-4">
                    <div>
                      <Label>Current Location</Label>
                      <Input
                        placeholder="City, State (e.g., Dallas, TX)"
                        value={formData.current_location}
                        onChange={(e) => setFormData({ ...formData, current_location: e.target.value })}
                        className="mt-2 bg-[#0A1628] border-gray-200"
                      />
                    </div>
                    <div>
                      <Label>Search Radius</Label>
                      <Select
                        value={formData.radius_miles}
                        onValueChange={(value) => setFormData({ ...formData, radius_miles: value })}
                      >
                        <SelectTrigger className="mt-2 bg-[#0A1628] border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          <SelectItem value="50">50 miles</SelectItem>
                          <SelectItem value="100">100 miles</SelectItem>
                          <SelectItem value="150">150 miles</SelectItem>
                          <SelectItem value="200">200 miles</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Load Categories */}
                <div>
                  <Label className="text-gray-600">Load Categories (optional)</Label>
                  <div className="flex gap-2 mt-2">
                    {['auto_transport', 'hotshot', 'freight'].map(cat => (
                      <button
                        key={cat}
                        onClick={() => toggleCategory(cat)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          formData.load_categories.includes(cat)
                            ? cat === 'auto_transport' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                              : cat === 'hotshot' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                              : 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : 'bg-white text-gray-600 border border-gray-200'
                        }`}
                      >
                        {cat === 'auto_transport' ? '🚗 Auto' : cat === 'hotshot' ? '⚡ Hotshot' : '📦 Freight'}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave empty for all categories</p>
                </div>

                {/* Price Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Min Price</Label>
                    <Input
                      type="number"
                      placeholder="$0"
                      value={formData.min_price}
                      onChange={(e) => setFormData({ ...formData, min_price: e.target.value })}
                      className="mt-2 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Max Price</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={formData.max_price}
                      onChange={(e) => setFormData({ ...formData, max_price: e.target.value })}
                      className="mt-2 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                </div>

                {/* Notification Method */}
                <div>
                  <Label className="text-gray-600">Notification Method</Label>
                  <Select
                    value={formData.notification_method}
                    onValueChange={(value) => setFormData({ ...formData, notification_method: value })}
                  >
                    <SelectTrigger className="mt-2 bg-[#0A1628] border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="in_app">In-App Only</SelectItem>
                      <SelectItem value="email">Email Only</SelectItem>
                      <SelectItem value="both">Email & In-App</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => { setShowCreateDialog(false); resetForm(); }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateAlert}
                    disabled={creating}
                    className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Alert'
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Alerts List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
          </div>
        ) : alerts.length === 0 ? (
          <Card className="border-gray-200 bg-white">
            <CardContent className="py-16 text-center">
              <Bell className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl font-semibold text-[#0D1B2A] mb-2">No Alerts Yet</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Create your first alert to get notified when loads match your preferred lanes or locations.
              </p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-[#4ADE80] hover:bg-[#22C55E] text-black"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Your First Alert
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <Card 
                key={alert.id} 
                className={`border-gray-200 bg-white transition-all ${
                  !alert.is_active ? 'opacity-60' : ''
                }`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Alert Type Icon */}
                      <div className={`p-3 rounded-xl ${
                        alert.alert_type === 'lane' 
                          ? 'bg-[#3B82F6]/20' 
                          : 'bg-[#4ADE80]/20'
                      }`}>
                        {alert.alert_type === 'lane' ? (
                          <Route className="w-6 h-6 text-[#3B82F6]" />
                        ) : (
                          <MapPin className="w-6 h-6 text-green-600" />
                        )}
                      </div>
                      
                      {/* Alert Info */}
                      <div>
                        <h3 className="text-lg font-semibold text-[#0D1B2A] flex items-center gap-2">
                          {alert.name}
                          {!alert.is_active && (
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-600 text-gray-300">Paused</span>
                          )}
                        </h3>
                        <div className="text-sm text-gray-600 mt-1">
                          {alert.alert_type === 'lane' ? (
                            <span className="flex items-center gap-2">
                              <span className="text-green-400">{alert.origin_state || alert.origin_city || alert.origin_region || 'Any'}</span>
                              <ChevronRight className="w-4 h-4" />
                              <span className="text-red-400">{alert.dest_state || alert.dest_city || alert.dest_region || 'Any'}</span>
                            </span>
                          ) : (
                            <span>Within {alert.radius_miles} miles of {alert.current_location}</span>
                          )}
                        </div>
                        {/* Categories */}
                        {alert.load_categories?.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {alert.load_categories.map(cat => (
                              <span 
                                key={cat}
                                className={`text-xs px-2 py-0.5 rounded ${
                                  cat === 'auto_transport' ? 'bg-blue-500/20 text-blue-400'
                                  : cat === 'hotshot' ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-green-500/20 text-green-400'
                                }`}
                              >
                                {cat === 'auto_transport' ? '🚗 Auto' : cat === 'hotshot' ? '⚡ Hotshot' : '📦 Freight'}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Side - Matches & Actions */}
                    <div className="flex items-center gap-6">
                      {/* Current Matches */}
                      <div className="text-center">
                        <div className={`text-2xl font-bold ${
                          alert.current_matches > 0 ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {alert.current_matches}
                        </div>
                        <div className="text-xs text-gray-500">matches</div>
                      </div>

                      {/* View Matches */}
                      {alert.current_matches > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/loadboard?alertId=${alert.id}`)}
                          className="border-[#3B82F6]/30 hover:bg-[#3B82F6]/10"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                      )}

                      {/* Toggle Active */}
                      <button
                        onClick={() => handleToggleAlert(alert.id, alert.is_active)}
                        className="text-gray-600 hover:text-[#0D1B2A] transition-colors"
                        title={alert.is_active ? 'Pause alert' : 'Activate alert'}
                      >
                        {alert.is_active ? (
                          <ToggleRight className="w-8 h-8 text-green-600" />
                        ) : (
                          <ToggleLeft className="w-8 h-8" />
                        )}
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => handleDeleteAlert(alert.id)}
                        className="text-gray-500 hover:text-red-400 transition-colors"
                        title="Delete alert"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="border-gray-200 bg-white/50 mt-8">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-[#3B82F6] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-[#0D1B2A] mb-1">How Backhaul Alerts Work</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Lane-based alerts</strong> notify you when loads are posted on your preferred routes (e.g., TX → CA)</li>
                  <li>• <strong>Location-based alerts</strong> notify you of loads near your current location</li>
                  <li>• You'll receive notifications based on your selected method (in-app, email, or both)</li>
                  <li>• Toggle alerts on/off without deleting them to control when you receive notifications</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AlertsPage;
